import { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import { MoveRight } from "lucide-react";
import { Input } from "../../../../../ui/input";
import type { DateRange } from "react-day-picker";
import type { RangeAnchorProps } from "./types";

function Range({ rangeSelected, setOpen, draftFrom, setDraftFrom, draftTo, setDraftTo, disabled, dateFormat, onChange }: RangeAnchorProps) {
    const { formattedFrom, formattedTo } = useMemo(() => {
        return {
            formattedFrom: rangeSelected?.from ? moment(rangeSelected?.from).format(dateFormat) : "",
            formattedTo: rangeSelected?.to ? moment(rangeSelected?.to).format(dateFormat) : ""
        }
    }, [dateFormat, rangeSelected?.from, rangeSelected?.to]);

    const commitText = useCallback((side: "from" | "to", text: string) => {
        if (side === "from") setDraftFrom(null);
        else setDraftTo(null);

        if (!text) {
            const next: DateRange = { from: rangeSelected?.from, to: rangeSelected?.to };
            next[side] = undefined;
            onChange?.(next.from || next.to ? next : undefined);
            return;
        }

        const parsed = moment(text, dateFormat, true);
        if (!parsed.isValid()) return;

        const next: DateRange = { from: rangeSelected?.from, to: rangeSelected?.to };
        next[side] = parsed.toDate();
        if (next.from && next.to && next.from.getTime() > next.to.getTime()) {
            const tmp = next.from;
            next.from = next.to;
            next.to = tmp;
        }
        onChange?.(next);
    }, [dateFormat, onChange, rangeSelected, setDraftFrom, setDraftTo]);

    return (
        <div
            className="tw-flex tw-h-9 tw-w-full tw-min-w-0 tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-input tw-bg-transparent tw-pr-14 tw-pl-2.5 tw-text-base
            tw-transition-colors focus-within:tw-outline-none focus-within:tw-ring-1
            focus-within:tw-ring-ring md:tw-text-sm has-[input:disabled]:tw-pointer-events-none has-[input:disabled]:tw-cursor-not-allowed has-[input:disabled]:tw-opacity-50"
        >
            <Input
                value={draftFrom ?? formattedFrom}
                placeholder="Start date"
                disabled={disabled}
                onChange={(e) => setDraftFrom(e.target.value)}
                onBlur={(e) => commitText("from", e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        commitText("from", e.currentTarget.value);
                    } else if (e.key === "Escape") {
                        setOpen(false);
                    }
                }}
                onFocus={() => setOpen(true)}
                className="tw-h-full tw-border-0 tw-w-full tw-min-w-0 tw-bg-transparent tw-text-sm tw-outline-none tw-shadow-none placeholder:tw-text-muted-foreground focus-visible:tw-ring-0 focus-visible:tw-border-0 focus-visible:tw-shadow-none"
            />
            <span className="tw-text-muted-foreground tw-shrink-0 tw-text-sm tw-mr-3">
                <MoveRight size={15} />
            </span>

            <Input
                value={draftTo ?? formattedTo}
                placeholder="End date"
                disabled={disabled}
                onChange={(e) => setDraftTo(e.target.value)}
                onBlur={(e) => commitText("to", e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        commitText("to", e.currentTarget.value);
                    } else if (e.key === "Escape") {
                        setOpen(false);
                    }
                }}
                onFocus={() => setOpen(true)}
                className="tw-h-full tw-border-0 tw-w-full tw-min-w-0 tw-bg-transparent tw-text-sm tw-outline-none tw-shadow-none placeholder:tw-text-muted-foreground focus-visible:tw-ring-0 focus-visible:tw-border-0 focus-visible:tw-shadow-none"
            />
        </div>
    )
}

export default Range;
